import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Lottie from 'lottie-react';
import { mqLargeMin } from 'styled/tools/media-queries';
import { useChartPlayback } from './animations';
import { MicrobiomeChart } from './data';

export const CHART_SCALE = 0.9;

// Reserves the chart's box so the card doesn't shift while the JSON loads
const Frame = styled.div<{
  $width: number;
  $height: number;
  $desktopScale: number;
}>`
  width: ${({ $width }) => $width * CHART_SCALE}px;
  max-width: 100%;
  aspect-ratio: ${({ $width, $height }) => `${$width} / ${$height}`};

  svg {
    display: block;
  }

  @media ${mqLargeMin} {
    width: ${({ $width, $desktopScale }) =>
      $width * $desktopScale * CHART_SCALE}px;
  }
`;

interface ChartAnimationProps {
  chart: MicrobiomeChart;
  productName: string;
  active: boolean;
  replayKey: string;
  desktopScale: number;
}

const ChartAnimation = ({
  chart,
  productName,
  active,
  replayKey,
  desktopScale,
}: ChartAnimationProps) => {
  const [animationData, setAnimationData] = useState<object | null>(null);
  // "Loaded" means the data has arrived; lottie-react fires DOMLoaded before its
  // listener attaches when given animationData, so that event can't signal it
  const { containerRef, lottieRef } = useChartPlayback(
    active,
    animationData !== null,
    replayKey,
  );

  useEffect(() => {
    fetch(`/lottie/cobiotics/${chart.animation}.json`)
      .then((res) => res.json())
      .then(setAnimationData)
      .catch(() => {});
  }, [chart.animation]);

  return (
    <Frame
      ref={containerRef}
      $width={chart.width}
      $height={chart.height}
      $desktopScale={desktopScale}
      role='img'
      aria-label={`Bar chart of ${chart.name}: control compared with ${productName}, ${chart.finding}.`}
    >
      {animationData && (
        <Lottie
          lottieRef={lottieRef}
          animationData={animationData}
          loop={false}
          autoplay={false}
          aria-hidden='true'
        />
      )}
    </Frame>
  );
};

export default ChartAnimation;
